import * as React from 'react';
import moment from 'moment';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { FormControlLabel } from '@mui/material';
import CustomizedSwitches from 'components/Features/CustomizedSwitch';
import Loader from 'components/Loader/Loader';
import { bindActionCreators, Dispatch } from 'redux';
import { updateExpenseExportModel } from 'actions/Companies/UpdateExpenseExportModel';
import { connectWithRouter } from 'helpers/connectWithRouter';
const styles = require('./EditExpenseExportPopup.pcss');

export type ExpenseExportModel = {
  id?: string | number;
  templateName?: string;
  templateSource?: string;
  templateType?: string;
  status?: string;
  errorNotificationsEmail?: string;
  lastExportDate?: string;
  fileExtension?: string;
  useLineItems?: boolean;
  useAllocations?: boolean;
};

type Props = {
  open: boolean;
  model: ExpenseExportModel | null;
  onClose: () => void;
  onSave: (model: ExpenseExportModel) => void;
  updateExpenseExportModel: Function;
};

const EditExpenseExportPopup = ({
  open,
  model,
  onClose,
  onSave,
  updateExpenseExportModel,
}: Props) => {
  const [form, setForm] = React.useState<ExpenseExportModel>({});
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    setForm({
      useLineItems: false,
      useAllocations: false,
      ...(model ?? {}),
    });
  }, [model]);

  const handleChange =
    (field: keyof ExpenseExportModel) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleToggle =
    (field: keyof ExpenseExportModel) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.checked }));
    };

  const handleSave = () => {
    setIsLoading(true);
    updateExpenseExportModel(
      form,
      () => {
        setIsLoading(false);
        onSave(form);
      },
      () => {
        setIsLoading(false);
      },
    );
  };

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={onClose}
    >
      <div className={styles.title}>
        {`Edit Expense Export: ${model?.id ?? ''}`}
        <IconButton
          color="inherit"
          onClick={onClose}
          className={styles.closeIcon}
          aria-label="Close"
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </div>
      <div className={styles.content}>
        <TextField
          fullWidth
          margin="normal"
          variant="outlined"
          label="Template Name"
          value={form.templateName ?? ''}
          onChange={handleChange('templateName')}
        />
        <TextField
          fullWidth
          margin="normal"
          variant="outlined"
          label="Template Source"
          value={form.templateSource ?? ''}
          onChange={handleChange('templateSource')}
        />
        <TextField
          fullWidth
          margin="normal"
          variant="outlined"
          label="Template Type"
          value={form.templateType ?? ''}
          onChange={handleChange('templateType')}
        />
        <TextField
          select
          fullWidth
          margin="normal"
          variant="outlined"
          label="Status"
          value={form.status ?? ''}
          onChange={handleChange('status')}
        >
          <MenuItem value="enabled">Enabled</MenuItem>
          <MenuItem value="disabled">Disabled</MenuItem>
          <MenuItem value="errored">Errored</MenuItem>
        </TextField>
        <TextField
          fullWidth
          margin="normal"
          variant="outlined"
          label="Email"
          value={form.errorNotificationsEmail ?? ''}
          onChange={handleChange('errorNotificationsEmail')}
        />
        <div className={styles.dateField}>
          <TextField
            fullWidth
            type="date"
            margin="normal"
            variant="outlined"
            label="Last Export Date"
            InputLabelProps={{ shrink: true }}
            value={
              form.lastExportDate
                ? moment(form.lastExportDate).format('YYYY-MM-DD')
                : ''
            }
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                lastExportDate: event.target.value
                  ? moment(event.target.value, 'YYYY-MM-DD').toISOString()
                  : '',
              }))
            }
          />
          <Tooltip title="Editing the date field can create duplicate expense exports for customer">
            <WarningAmberIcon
              className={styles.warningIcon}
              color="warning"
            />
          </Tooltip>
        </div>
        <TextField
          fullWidth
          margin="normal"
          variant="outlined"
          label="File Extension"
          value={form.fileExtension ?? ''}
          onChange={handleChange('fileExtension')}
        />
        <div className={styles.toggleRow}>
          <span className={styles.toggleLabel}>Use Line Items</span>
          <CustomizedSwitches
            handleClick={handleToggle('useLineItems')}
            enabled={Boolean(form.useLineItems)}
            hasPermission={false}
          />
        </div>
        <div className={styles.toggleRow}>
          <span className={styles.toggleLabel}>Use Allocations</span>
           <CustomizedSwitches
            handleClick={handleToggle('useAllocations')}
            enabled={Boolean(form.useAllocations)}
            hasPermission={false}
          />
        </div>
      </div>
      <div className={styles.actions}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isLoading}
        >
          Save
        </Button>
      </div>
      {isLoading && (
        <div className={styles.loader}>
          <Loader visible={true} />
        </div>
      )}
    </Dialog>
  );
};

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators({ updateExpenseExportModel }, dispatch);
}

export default connectWithRouter(null, mapDispatchToProps)(EditExpenseExportPopup);
